/**
 * Маппинг tool-name → понятный для пользователя текст шага.
 * Инструменты Claude Agent SDK, которые агент использует в чате KOMMA Ads.
 */
import type { AiToolCall } from '@/state/aiStore';
import type { AiThinkingStep } from './AiThinking';

const TOOL_LABELS: Record<string, string> = {
  Skill: 'Открываю скилл',
  Bash: 'Запускаю скилл / команду',
  Read: 'Читаю файл проекта',
  Grep: 'Ищу по проекту',
  Glob: 'Ищу файлы',
  Write: 'Готовлю изменения',
  Edit: 'Готовлю изменения',
  TodoWrite: 'Планирую шаги',
  WebFetch: 'Смотрю страницу в вебе',
  WebSearch: 'Ищу в вебе',
  ToolSearch: 'Подбираю инструмент',
  'mcp__komma-memory__save_memory': 'Сохраняю в память проекта',
  'mcp__komma-exports__offer_download': 'Готовлю файл к скачиванию',
};

export function toolStepLabel(toolName: string): string {
  if (TOOL_LABELS[toolName]) return TOOL_LABELS[toolName];
  if (toolName.startsWith('mcp__')) return 'Использую инструмент';
  return `Использую инструмент: ${toolName}`;
}

/**
 * Преобразовать tool_calls сообщения + флаг ожидания текста в шаги для AiThinking.
 *
 * Логика:
 *   - Каждый toolCall = один шаг
 *   - status='running' → 'current'
 *   - status='done' → 'done'
 *   - status='error' → 'done' (визуально ок, ошибка покажется в текстовом ответе)
 *   - Если isStreaming, нет running tool'ов, и нет текста — добавляем «Готовлю ответ» как current
 */
export function toolCallsToSteps(
  toolCalls: AiToolCall[],
  isStreaming: boolean,
  hasText: boolean,
): AiThinkingStep[] {
  // Схлопываем одинаковые шаги: серия из нескольких вызовов одного инструмента
  // (например, подряд несколько `query`) — это ОДИН крутящийся шаг, а не повтор
  // одного и того же текста N раз. Порядок — по первому появлению.
  const order: string[] = [];
  const seen = new Set<string>();
  const running = new Set<string>();
  for (const t of toolCalls) {
    const title = toolStepLabel(t.name);
    if (!seen.has(title)) {
      seen.add(title);
      order.push(title);
    }
    if (t.status === 'running') running.add(title);
  }
  const steps: AiThinkingStep[] = order.map((title) => ({
    title,
    status: running.has(title) ? 'current' : 'done',
  }));

  const hasCurrent = steps.some((s) => s.status === 'current');
  if (isStreaming && !hasCurrent && !hasText) {
    steps.push({ title: 'Готовлю ответ', status: 'current' });
  }
  return steps;
}
